import { createFileRoute, Link } from "@tanstack/react-router";

import { MessagesSquare, FileText, Handshake } from "lucide-react";
import { Layout } from "@/components/Layout";
import { Reveal } from "@/components/Reveal";
import { BookingCtaButton, INQUIRY_CTA_LABEL } from "@/components/cta/BookingCtaButton";
import { ScrollRevealText } from "@/components/ScrollRevealText";
import { lazySection } from "@/lib/lazy-section";
import { BarChartAnimation } from "@/components/personalratswahl/BarChartAnimation";
import StickersAnimation from "@/components/personalratswahl/StickersAnimation";
import { CaseSpotlight } from "@/components/personalratswahl/CaseSpotlight";
import { testimonialsQueryOptions, casesByTagQueryOptions } from "@/lib/public-content.queries";
import teaserDpolg from "@/assets/teaser-dpolg.webp.asset.json";

const ContactCta = lazySection(async () => {
  const m = await import("@/components/personalratswahl/ContactCta");
  return { default: m.ContactCta };
}, { minHeight: 400 });
const ProzessTimeline = lazySection(async () => {
  const m = await import("@/components/buergermeisterwahl/ProzessTimeline");
  return { default: m.ProzessTimeline };
}, { minHeight: 600 });
import michaelBusserPortrait from "@/assets/michael-busser-quadratisch.png.asset.json";

import designAsset from "@/assets/design-2.svg.asset.json";
import socialMediaAsset from "@/assets/social-media-v2.webp.asset.json";
import michaelBusserAsset from "@/assets/parteikampagnen/michael-busser.png.asset.json";
import mobilisierungAsset from "@/assets/personalratswahl/creategy-mitglieder-3.svg.asset.json";
import analyseIlluAsset from "@/assets/parteikampagnen/analyse-strategie-illu.svg.asset.json";
import teamRioAsset from "@/assets/parteikampagnen/team-rio.png.asset.json";
import sicherheitDurchErfahrungAsset from "@/assets/buergermeisterwahl/sicherheit-durch-erfahrung.png.asset.json";
import modularUndFairAsset from "@/assets/buergermeisterwahl/modular-und-fair.png.asset.json";
import direktmandatZuUnsAsset from "@/assets/buergermeisterwahl/direktmandat-zu-uns.png.asset.json";
import beideWeltenAsset from "@/assets/personalratswahl/beide-welten.png.asset.json";
import wahlbeteiligungAsset from "@/assets/personalratswahl/wahlbeteiligung.png.asset.json";


export const Route = createFileRoute("/wahlen-gewinnen/personalratswahl")({
  // Ticket W7: Herkunftskennzeichnung vom Verteiler /wahlkampfagentur (?von=...).
  validateSearch: (search: Record<string, unknown>): { von?: string } => ({
    von: typeof search.von === "string" ? search.von : undefined,
  }),
  // Testimonials und Cases speisen nur lazy nachgeladene Below-the-Fold-Sektionen
  // → Prefetch anstoßen, aber nicht awaiten (kürzere TTFB / kritische Kette).
  loader: async ({ context }) => {
    void context.queryClient.prefetchQuery(testimonialsQueryOptions);
    void context.queryClient.prefetchQuery(casesByTagQueryOptions("personalratswahl"));
  },
  head: () => {
    const title = "Personalratswahl gewinnen | creategy";
    const description =
      "Gewinnen Sie Ihre Personalratswahl. Professionell. Wir bringen das Handwerk echter Wahlkämpfe in Ihre Kampagne. Für Gewerkschaften und Berufsverbände.";
    const url = "https://creategy.de/wahlen-gewinnen/personalratswahl";
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:type", content: "website" },
        { property: "og:url", content: url },
        { property: "og:site_name", content: "creategy" },
        { property: "og:locale", content: "de_DE" },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: title },
        { name: "twitter:description", content: description },
      ],
      links: [{ rel: "canonical", href: url }],
    };
  },
  component: PersonalratswahlPage,
});

function PersonalratswahlPage() {
  const gruende = [
    {
      title: "Sicherheit durch Erfahrung.",
      text: "Wir haben abgestimmt: nur Politik. Über zweihundert Wahlkämpfe, davon zahlreiche Personalratswahlen für Gewerkschaften und Berufsverbände. Wer zu uns kommt, bekommt kein Team, das sich einarbeitet, sondern eines, das Wahlen gewinnen kann.",
      image: sicherheitDurchErfahrungAsset.url,
      imageAlt: "Strategieentwicklung auf Whiteboard als Symbol für Erfahrung",
    },
    {
      title: "Wir kennen beide Welten.",
      text: "Personalratswahlen brauchen Kampagnenhandwerk und Verständnis für Gewerkschaftsstrukturen. Wir bringen beides mit: die Schlagkraft aus politischen Wahlkämpfen und die Erfahrung aus der Zusammenarbeit mit Berufsverbänden wie der DPolG und dem Marburger Bund.",
      image: beideWeltenAsset.url,
      imageAlt: "Zwei ineinandergreifende Holz-Puzzleteile als Symbol für die Verbindung von Kampagnenhandwerk und Gewerkschaftserfahrung",
    },
    {
      title: "Modular und fair.",
      text: "Sie buchen nur, was Sie brauchen. Kein aufgeblähtes Paket, keine versteckten Kosten, kein Upselling im falschen Moment. Transparentes Pricing, das zu Ihrer Organisation und Ihrem Budget passt.",
      image: modularUndFairAsset.url,
      imageAlt: "Gelber Baustein wird auf eine graue Mauer gesetzt – Symbol für modulare Bausteine",
    },
    {
      title: "Wahlbeteiligung schaffen.",
      text: "Personalratswahlen werden nicht über Argumente entschieden, sondern über Beteiligung. Wer seine Unterstützer nicht zur Wahl bringt, verliert trotz besserer Liste. Mobilisierung ist genau das, was wir aus hundert politischen Wahlkämpfen am besten können.",
      image: wahlbeteiligungAsset.url,
      imageAlt: "Hand wirft Stimmzettel in eine weiße Wahlurne als Symbol für Wahlbeteiligung",
    },
    {
      title: "Ihr Direktmandat zu uns.",
      text: 'Kein Ticketsystem, keine Warteschleife, kein „Ich leite das weiter." Bei creategy haben Sie einen festen Ansprechpartner: erreichbar per Telefon, Mail und WhatsApp. Auch kurz vor dem Wahltermin. Gerade dann.',
      image: direktmandatZuUnsAsset.url,
      imageAlt: "Persönlicher Ansprechpartner am Telefon",
    },
  ];

  const gruendeImages = gruende.map((card) => ({ src: card.image, alt: card.imageAlt }));

  return (
    <Layout>
      {/* Hero */}
      <section aria-labelledby="hero">
        <div className="section-y-hero mx-auto w-full max-w-[1400px] px-6 md:px-12">
          {/* Mobile-only animation above headline */}
          <div className="mx-auto mb-8 w-full max-w-[420px] md:hidden">
            <BarChartAnimation />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 lg:items-center lg:gap-16">
            <div className="relative">
              <h1
                id="hero"
                className="heading-h1 text-brand-wahlen"
              >
                Mit professioneller Unterstützung zur<br />stärksten Liste.
              </h1>

              {/* Tablet: text+CTA left, animation right; Mobile & Desktop: single column */}
              <div className="mt-10 grid grid-cols-1 gap-10 md:grid-cols-2 md:items-center md:gap-10 lg:grid-cols-1">
                <div>
                  <p
                    className="fade-in-up font-body text-base leading-relaxed text-foreground md:text-lg"
                    style={{ animationDelay: "160ms" }}
                  >
                    Als Spezialagentur für Personalratswahlen unterstützen wir
                    Gewerkschaften und Berufsverbände bei ihrer Personalratswahl, mit
                    Kampagnenerfahrung, die andere nicht haben.
                  </p>
                  <div
                    className="fade-in-up mt-10 flex flex-col items-stretch gap-3 lg:flex-row lg:items-center"
                    style={{ animationDelay: "240ms" }}
                  >
                    <Link
                      to="/kontakt"
                      search={{ anliegen: "personalratswahl" }}
                      className="inline-flex w-full items-center justify-center rounded-md bg-brand-button px-6 py-3 font-display text-sm font-extrabold text-brand-button-foreground transition-opacity hover:opacity-90 lg:w-auto"
                    >
                      {INQUIRY_CTA_LABEL}
                    </Link>
                    <BookingCtaButton variant="secondary" eventPath="creategy/erstgespraech" />
                  </div>
                </div>
                <div className="hidden md:block lg:hidden">
                  <div className="mx-auto w-full max-w-[380px]">
                    <BarChartAnimation />
                  </div>
                </div>
              </div>
            </div>

            {/* Desktop-only animation, right column */}
            <div className="hidden w-full max-w-[420px] lg:ml-auto lg:block">
              <BarChartAnimation />
            </div>
          </div>
        </div>
      </section>


      {/* Warum professionelle Unterstützung */}
      <section aria-labelledby="warum-unterstuetzung">
        <div className="section-y mx-auto w-full max-w-[1400px] px-6 md:px-12">
          <div>
            <Reveal>
              <h2
                id="warum-unterstuetzung"
                className="heading-h2 text-brand-wahlen"
              >
                Warum Personalratswahlen{"\n"}professionelle Unterstützung benötigen.
              </h2>
            </Reveal>
            <Reveal delay={60}>
              <div className="heading-lead grid gap-12 md:grid-cols-2 md:items-center">
                <div>
                  <h3 className="heading-h3 text-brand-wahlen">
                    Der Wahlkampf am schwarzen Brett hat ausgedient
                  </h3>
                  <p className="mt-6 font-body text-base leading-relaxed text-foreground md:text-lg">
                    Lange war es einfach: Liste aufstellen, Aushänge am Schwarzen Brett, fertig. Doch Personalratswahlen werden heute professionell geführt. Konkurrierende Listen investieren in Kommunikation und die Erwartungen der Mitglieder an ihre Interessenvertretung steigen. Wer in diesem Umfeld gewinnen will, braucht eine klare Strategie und eine moderne Wahlkampagne.
                  </p>
                  <p className="mt-6 font-body text-base leading-relaxed text-foreground md:text-lg">
                    creategy bringt das Know-How politischer Wahlkämpfe in Ihre Personalratswahl. Klare Botschaften, ein professionelles Auftreten und Kommunikation, die Mitglieder zur Wahl bewegt.
                  </p>
                </div>
                <StickersAnimation className="order-first md:order-none mx-auto mt-2 md:mt-0 aspect-square w-full max-w-[280px] md:max-w-[380px]" />
              </div>
            </Reveal>

          </div>
        </div>
      </section>


      <section aria-labelledby="spotlight">
        <div className="section-y mx-auto w-full max-w-[1400px] px-6 md:px-12">
          <Reveal>
            <h2 id="spotlight" className="heading-h2 text-brand-wahlen">
              Ein Projekt, das zeigt, wie wir arbeiten.
            </h2>
          </Reveal>
          <div className="mt-16">
            <CaseSpotlight
              subline="Deutsche Polizeigewerkschaft (DPolG)"
              title="Strategisch zum Wahlerfolg: Personalratswahlen mit der DPolG Bayern."
              text="Für den Landesverband Bayern der Deutschen Polizeigewerkschaft haben wir die Personalratswahl strategisch begleitet: mit einer fundierten Analyse der letzten Wahl, einer maßgeschneiderten Wahlkampfstrategie sowie Konzeption und Texten für sämtliche Werbemittel. Regierungssprecher a.D. Michael Bußer hat die Kandidatinnen und Kandidaten für Rhetorik und Medienauftritte fit gemacht."
              href="/cases/dpolg-bayern"
              mediaLeft
            >
              <img
                src={teaserDpolg.url}
                alt="Kampagnenmotiv DPolG Bayern – Für Dich am dransten"
                loading="lazy"
                decoding="async"
                className="aspect-[4/3] w-full rounded-md object-cover object-center"
              />

            </CaseSpotlight>
          </div>
        </div>
      </section>

      {/* Bausteine */}
      <section aria-labelledby="bausteine">
        <div className="section-y mx-auto w-full max-w-[1400px] px-6 md:px-12">
          <Reveal>
            <h2
              id="bausteine"
              className="heading-h2 text-brand-wahlen"
            >
              Die Bausteine Ihrer Personalratswahl-Kampagne.
            </h2>
          </Reveal>
          <Reveal>
            <p className="mt-8 font-body text-base leading-relaxed text-foreground md:text-lg">
              Jede Personalratswahl hat ihre eigene Dynamik. Wir entwickeln modulare Kampagnen, die zu Ihrer Organisation, Ihren Mitgliedern und Ihrem Wahltermin passen.
            </p>
          </Reveal>
          <div className="mt-12 grid gap-6 md:grid-cols-2 lg:grid-cols-6">
            {([
              { title: "Strategie & Analyse", text: "Wer sind Ihre Mitglieder, was bewegt sie und wer tritt gegen Sie an? Wir analysieren Ihre Ausgangslage und entwickeln eine passende Kampagnenstrategie.", rightBottomImage: analyseIlluAsset.url, rightBottomImageAlt: "Illustration: Strategie & Analyse" },
              { title: "Kampagnendesign & Print", text: "Personalratswahlen brauchen ein Gesicht. Wir entwickeln ein unverwechselbares Design für Ihre Liste und bringen es in Umlauf: vom klassischen Plakat bis zum raffinierten Wahlkampfgoodie.", image: designAsset.url, imageAlt: "Illustration als Symbol für Kampagnendesign", imageClassName: "max-h-[8rem] md:max-h-[9rem] lg:max-h-[12rem]" },
              { title: "Operative Kampagnenbegleitung", text: "Manchmal fehlt keine Idee, sondern eine Hand, die anpackt. Wir übernehmen die laufende Umsetzung Ihrer Kampagne.", image: teamRioAsset.url, imageAlt: "Rio (creategy) am Laptop – Symbol für operative Kampagnenbegleitung", imageClassName: "max-h-[12rem] md:max-h-[13rem] lg:max-h-[12rem]" },
              { title: "Social-Media-Kommunikation", text: "Auch Personalratswahlen werden heute digital gewonnen. Wir entwickeln digitale Inhalte, die Ihre Wählerinnen und Wähler dort erreichen, wo sie wirklich sind.", coverImage: socialMediaAsset.url, coverImageAlt: "Haufen roter Like- und Reaktions-Icons als Symbol für Social-Media-Engagement im Wahlkampf" },
              { title: "Mitgliedermobilisierung", text: "Die Wahlbeteiligung entscheidet oft mehr als die Stimmenverteilung. Wir entwickeln Formate, die Ihre Mitglieder aktiv zur Wahl bringen.", image: mobilisierungAsset.url, imageAlt: "Illustration eines Netzwerks aus verbundenen Personen als Symbol für Mitgliedermobilisierung", imageClassName: "max-h-[12rem] md:max-h-[13rem] lg:max-h-[12rem]" },
              { title: "Rhetoriktraining", text: "Kandidatenvorstellungen und Betriebsversammlungen entscheiden Wahlen. Wir bereiten Ihre Kandidatinnen und Kandidaten auf genau diese Momente vor.", leftBottomImage: michaelBusserAsset.url, leftBottomImageAlt: "Michael Bußer, Regierungssprecher a.D., beim Rhetorik- und Auftrittstraining" },
            ] as Array<{ title: string; text: string; image?: string; imageAlt?: string; imageClassName?: string; coverImage?: string; coverImageAlt?: string; rightBottomImage?: string; rightBottomImageAlt?: string; leftBottomImage?: string; leftBottomImageAlt?: string }>).map((b, i) => {

              const span = "lg:col-span-2";
              return (
                <Reveal key={b.title} delay={i * 60} className={`${span} h-full`}>
                  {b.rightBottomImage || b.leftBottomImage ? (
                    <article className="flex h-[20rem] flex-col overflow-hidden rounded-md bg-brand-wahlen text-background md:h-[24rem]">
                      <div className="px-6 pt-6 md:px-8 md:pt-8">
                        <h3 className="heading-h3 text-left text-background">
                          {b.title}
                        </h3>
                      </div>

                      <div className="mt-4 flex min-h-0 flex-1 flex-row items-stretch gap-4 md:gap-6">
                        {b.leftBottomImage ? (
                          <>
                            <div className="flex h-full max-w-[32%] shrink-0 items-end md:max-w-[38%] lg:max-w-[45%]">
                              <img
                                src={b.leftBottomImage}
                                alt={b.leftBottomImageAlt ?? ""}
                                className="block h-full w-auto max-w-full object-contain object-left-bottom"
                                decoding="async"
                              />
                            </div>
                            <p className="min-w-0 flex-1 pb-6 pr-6 font-body text-sm leading-relaxed text-background hyphens-auto-de text-pretty md:pb-8 md:pr-8">
                              {b.text}
                            </p>
                          </>
                        ) : (
                          <>
                            <p className="min-w-0 flex-1 pb-6 pl-6 font-body text-sm leading-relaxed text-background hyphens-auto-de text-pretty md:pb-8 md:pl-8">
                              {b.text}
                            </p>
                            <div className="flex h-full max-w-[45%] shrink-0 items-start md:max-w-[52%] lg:max-w-[45%]">
                              <img
                                src={b.rightBottomImage}
                                alt={b.rightBottomImageAlt ?? ""}
                                className="block h-full w-auto max-w-full object-contain object-right-top"
                                decoding="async"
                              />
                            </div>
                          </>
                        )}
                      </div>
                    </article>
                  ) : (
                    <article className="flex h-[20rem] flex-col overflow-hidden rounded-md bg-brand-wahlen p-6 text-background md:h-[24rem] md:p-8">
                      <h3 className="heading-h3 text-background">
                        {b.title}
                      </h3>
                      <p className="mt-4 font-body text-sm leading-relaxed text-background hyphens-auto-de text-pretty">
                        {b.text}
                      </p>
                      {b.image ? (
                        <div className="-mx-6 -mb-10 mt-auto flex min-h-0 flex-1 items-end justify-center md:-mx-8 md:-mb-12">
                          <img
                            src={b.image}
                            alt={b.imageAlt ?? ""}
                            className={`block h-auto w-full max-w-full object-contain object-bottom md:w-auto ${b.imageClassName ?? "max-h-[8rem] md:max-h-[13rem] lg:max-h-[12rem]"}`}
                            decoding="async"
                          />
                        </div>
                      ) : b.coverImage ? (
                        <div className="-mx-6 -mb-10 mt-auto flex min-h-0 flex-1 items-end overflow-hidden max-h-[9rem] md:-mx-8 md:-mb-12 md:max-h-[18rem] lg:max-h-[18rem]">
                          <img
                            src={b.coverImage}
                            alt={b.coverImageAlt ?? ""}
                            className="block h-full w-full object-cover object-top"
                            decoding="async"
                          />
                        </div>
                      ) : (
                        <div
                          aria-hidden="true"
                          className="-mx-6 -mb-10 mt-auto flex min-h-0 flex-1 items-center justify-center border-t border-dashed border-border bg-secondary/40 md:-mx-8 md:-mb-12"
                        >
                          <span className="font-display text-xs uppercase tracking-wider text-muted-foreground">
                            Bild folgt
                          </span>
                        </div>
                      )}
                    </article>
                  )}
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>


      {/* Michael Bußer */}
      <section aria-labelledby="michael-busser">
        <div className="section-y mx-auto w-full max-w-[1400px] px-6 md:px-12">
          <Reveal>
            <h2
              id="michael-busser"
              className="heading-h2 text-brand-wahlen"
            >
              Bauen Sie auf die Erfahrung eines Regierungs{"\u00AD"}sprechers
            </h2>
          </Reveal>
          <Reveal delay={60}>
            <p className="mt-8 font-body text-base leading-relaxed text-foreground md:text-lg">
              Michael Bußer hat als Regierungssprecher und Staatssekretär a.D. in Situationen kommuniziert, in denen jedes Wort zählte. Dieses Wissen gibt er direkt an unsere Kundinnen und Kunden im Zuge der Personalratswahl-Kampagne weiter.
            </p>
          </Reveal>
          <Reveal delay={120}>
            <div className="mt-12 grid gap-8 md:grid-cols-[minmax(0,320px)_minmax(0,1fr)] md:items-center">
              <div className="mx-auto w-full max-w-[320px] overflow-hidden rounded-md bg-transparent md:mx-0">
                <img
                  src={michaelBusserPortrait.url}
                  alt="Michael Bußer"
                  className="h-auto w-full object-contain"
                  loading="lazy"
                />
              </div>
              <div className="flex flex-col justify-center">
                <ScrollRevealText
                  text={'„Personalratswahlen werden auf Betriebsversammlungen, in Kandidatenvorstellungen und in direkten Gesprächen mit Kolleginnen und Kollegen entschieden. Wir zeigen Ihnen, wie Sie Vertrauen schaffen."'}
                  className="font-body text-xl leading-snug text-brand-wahlen md:text-2xl lg:text-3xl"
                />

                <p className="mt-6 font-display text-sm font-extrabold text-brand-wahlen">
                  Michael Bußer
                </p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* 5 Gründe für creategy – Sticky-Stack der Karten, Bild links parallel sticky */}
      <section
        aria-labelledby="gruende"
        className="relative z-0 isolate bg-background text-foreground"
      >
        <div className="section-y mx-auto w-full max-w-[1400px] px-6 md:px-12">
          <div className="mb-6 md:mb-10">
            <Reveal>
              <h2 id="gruende" className="heading-h2 text-brand-wahlen">
                5 Gründe für creategy
              </h2>
            </Reveal>
            <Reveal delay={80}>
              <p className="mt-4 font-body text-sm leading-relaxed text-foreground/85 md:text-base">
                Warum Gewerkschaften und Berufsverbände sich für creategy entscheiden:
                fünf Gründe, die in der Personalratswahl den Unterschied machen.
              </p>
            </Reveal>
          </div>

          <ol className="flex flex-col gap-4 md:gap-0">
            {gruende.map((card, i) => {
              const img = gruendeImages[i];
              return (
                <li
                  key={card.title}
                  className="md:sticky md:top-32 md:pb-[6.66vh]"
                  style={{ zIndex: 10 + i }}
                >
                  <article className="grid gap-8 rounded border border-brand-wahlen/20 bg-background p-7 shadow-[0_10px_30px_-12px_rgba(0,75,81,0.18)] md:min-h-[28rem] md:grid-cols-[minmax(0,1.5fr)_minmax(0,3.5fr)] md:items-stretch md:gap-8 md:p-9 lg:min-h-0">
                    <div className="relative aspect-square w-full max-w-[360px] overflow-hidden rounded border border-brand-wahlen/20 bg-brand-wahlen/5">
                      <img
                        src={img.src}
                        alt={img.alt ?? ""}
                        className="h-full w-full object-cover"
                        loading="lazy"
                        decoding="async"
                      />
                    </div>
                    <div className="flex h-full flex-col justify-center">
                      <div>
                        <h3 className="heading-h3 text-brand-wahlen">
                          {card.title}
                        </h3>
                        <p className="mt-4 font-body text-base leading-relaxed text-foreground/85">
                          {card.text}
                        </p>
                      </div>
                      <div className="mt-6 font-display text-sm font-extrabold tracking-wider text-brand-wahlen/60">
                        Grund {i + 1} von {gruende.length}
                      </div>
                    </div>
                  </article>
                </li>
              );
            })}
          </ol>
        </div>
      </section>

      {/* Prozess */}
      <section aria-labelledby="prozess">
        <div className="section-y mx-auto w-full max-w-[1400px] px-6 md:px-12">
          <Reveal>
            <h2 id="prozess" className="heading-h2 text-brand-wahlen">
              In drei Schritten{" "}
              zur Zusammenarbeit.
            </h2>
          </Reveal>

          <div className="mt-16 md:mt-20">
            <ProzessTimeline
              headingColor="text-brand-wahlen"
              steps={[
                {
                  title: "Kostenloses Erstgespräch",
                  text: "Wir lernen Ihre Organisation kennen. Sie erzählen uns von Ihrem Wahltermin, Ihrer Ausgangslage und Ihren Zielen. Wir zeigen Ihnen, wie eine professionelle Personalratswahl-Kampagne aussehen kann. Digital oder bei Ihnen vor Ort.",
                  Icon: MessagesSquare,
                },
                {
                  title: "Ihr individuelles Angebot",
                  text: "Auf Basis des Gesprächs entwickeln wir ein maßgeschneidertes Angebot mit genau den Bausteinen, die Ihre Kampagne braucht. Abgestimmt auf Ihre Organisation, Ihre Mitgliederstruktur und Ihr Budget.",
                  Icon: FileText,
                },
                {
                  title: "Ihre Entscheidung",
                  text: 'Sie entscheiden, ob und in welchem Umfang wir zusammenarbeiten. In Ruhe und mit allen Gremien, die dazugehören. Wenn Sie Ja sagen, legen wir los. Im Zweifel in sieben Tagen.',
                  Icon: Handshake,
                },
              ]}
            />
          </div>
        </div>
      </section>
      <ContactCta />
    </Layout>
  );
}
